"use client";

import { PortfolioDraftQuery } from "@/hooks/queries/portfolio/use-portfolio-draft-query";
import { StorageKeys, StorageService } from "@/services/storage-service";
import { useSession } from "next-auth/react";
import React, { PropsWithChildren, createContext } from "react";

type StorageProviderContextType = {
  userId: {
    value?: string;
    update: (value: string) => void;
  };
};

const StorageProviderContext = createContext<
  StorageProviderContextType | undefined
>(undefined);

export const StorageProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { data: session } = useSession();
  const [userId, setUserId] = React.useState<string | undefined>(
    StorageService.get(StorageKeys.USER_ID)?.toString() ?? undefined,
  );

  const { data: portfolioDraft } = PortfolioDraftQuery.useQuery(
    userId,
    undefined,
    {
      enabled: !!userId,
    },
  );

  React.useEffect(() => {
    if (!session?.user.id) {
      return;
    }

    // In case the user tried to create a portfolio before logging in,
    // we need to update the userId in the portfolio draft
    if (session.user.id && userId && portfolioDraft) {
      PortfolioDraftQuery.setData(
        session.user.id,
        portfolioDraft.tournamentId ?? undefined,
        portfolioDraft,
      );

      PortfolioDraftQuery.remove(userId);
      StorageService.remove(StorageKeys.USER_ID);
      setUserId(undefined);
      // Lastly, invalidate the logged users portfolio draft to ensure the latest data is fetched
      PortfolioDraftQuery.invalidate(
        session.user.id,
        portfolioDraft.tournamentId ?? undefined,
      );
    }
  }, [portfolioDraft, session?.user.id, userId]);

  return (
    <StorageProviderContext.Provider
      value={{ userId: { value: userId, update: setUserId } }}
    >
      {children}
    </StorageProviderContext.Provider>
  );
};

export const useStorageProviderContext = () => {
  const context = React.useContext(StorageProviderContext);
  if (context === undefined) {
    throw new Error(
      `useStorageProviderContext must be used within a StorageProvider`,
    );
  }
  return context;
};
